import { useTranslations } from "next-intl";
import { Field } from "@/types/Field";
import { CreationFieldsRef } from "../PCreateGuest";
import { evaluateConditionGroup } from "@/lib/utils/evaluate-condition";
import { PDeleteConfirmation } from "../../PDeleteConfirmation/PDeleteConfirmation";
import { hasSuppliedFieldValue } from "@/lib/utils/input-validator";

/**
 * Soft required-field check for the admin guest popup. Returns the required
 * fields the admin left empty — SKIPPING ones that are hidden/locked by an
 * unmet condition (not on the form for this guest) or that carry a default
 * (the backend fills it). The admin popup only WARNS (doesn't hard-block);
 * the public form + backend enforce required strictly.
 */
export function getMissingRequiredFields(
  creationFields: CreationFieldsRef,
  allFields: Field[],
): Field[] {
  const additionalFields: Record<string, unknown> = {
    ...(creationFields.additionalFields ?? {}),
  };
  for (const f of allFields) {
    // Blank-aware — see `hasSuppliedFieldValue`. A presence test made this
    // warning disagree with the backend's own required check for `""`.
    if (
      f.additionalField &&
      !hasSuppliedFieldValue(additionalFields, f.name) &&
      f.defaultValue != null
    ) {
      additionalFields[f.name] = f.defaultValue;
    }
  }
  const evalData = { ...creationFields, additionalFields };

  const isEmpty = (v: unknown) =>
    v == null ||
    (typeof v === "string" && v.trim() === "") ||
    (Array.isArray(v) && v.length === 0);

  const missing: Field[] = [];
  for (const f of allFields) {
    if (!f.required) continue;
    // Credentials (username/code) are backend-generated, not user-filled (and
    // they have no input at create) — never warn for them, like a defaulted
    // field.
    if (!f.additionalField && (f.name === "username" || f.name === "code"))
      continue;
    // A field carrying a default is never "missing" — the backend auto-fills it.
    if (f.defaultValue != null && f.defaultValue !== "") continue;
    // Hidden/locked by an unmet condition → not on the form for this guest.
    const hidden =
      (f.visibilityCondition &&
        !evaluateConditionGroup(f.visibilityCondition, evalData)) ||
      (f.editableCondition &&
        !evaluateConditionGroup(f.editableCondition, evalData));
    if (hidden) continue;
    const val = f.additionalField
      ? creationFields.additionalFields?.[f.name]
      : (creationFields as unknown as Record<string, unknown>)[f.name];
    if (isEmpty(val)) missing.push(f);
  }
  return missing;
}

/**
 * Soft warning shown when the admin saves a guest with empty required fields.
 * Non-blocking: lists the fields and lets the admin save anyway.
 */
export const RequiredFieldsWarnDialog: React.FC<{
  fields: Field[];
  loading: boolean;
  onClose: () => void;
  onConfirm: () => void;
}> = ({ fields, loading, onClose, onConfirm }) => {
  const t = useTranslations("popup-create-guest");
  const tField = useTranslations("guest-fields");
  const labelOf = (f: Field) =>
    f.additionalField
      ? f.name
      : tField.has(f.name)
        ? tField(f.name)
        : f.name;
  return (
    <PDeleteConfirmation
      title={t("requiredEmptyTitle")}
      message={t("requiredEmptyMessage")}
      primaryButtonText={t("requiredEmptyConfirm")}
      isLoading={loading}
      onCloseAction={onClose}
      onConfirmAction={async () => onConfirm()}
    >
      <ul className="list-disc pl-5 body-3-regular font-secondary space-y-0.5">
        {fields.map((f) => (
          <li key={f.fieldId ?? f.name}>{labelOf(f)}</li>
        ))}
      </ul>
    </PDeleteConfirmation>
  );
};
